import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { use2FAAuth } from "@/hooks/use2FAAuth";

const Admin2FAVerification = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { mark2FAVerified } = use2FAAuth();
  
  const [code, setCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [useBackupCode, setUseBackupCode] = useState(false);

  const handleVerify = async () => {
    if (!code || code.length !== 6) {
      toast({
        title: "Código inválido",
        description: "Digite um código de 6 dígitos",
        variant: "destructive",
      });
      return;
    }

    setIsVerifying(true);

    try {
      const { data, error } = await supabase.functions.invoke('verify-2fa', {
        body: { 
          code,
          isBackupCode: useBackupCode 
        }
      });

      if (error) throw error;

      if (data.success) {
        mark2FAVerified();
        toast({
          title: "Autenticação confirmada",
          description: "Acesso liberado ao painel admin",
        });
        navigate('/paineladmin');
      } else {
        toast({
          title: "Código incorreto",
          description: data.error || "Verifique o código e tente novamente",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      console.error('Erro ao verificar 2FA:', error);
      toast({
        title: "Erro na verificação",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-100" />
      
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div 
            className="backdrop-blur-xl bg-white/70 rounded-2xl shadow-2xl border border-white/20 p-8 space-y-6"
            style={{
              boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
            }}
          >
            <div className="flex justify-center">
              <div className="p-4 bg-blue-500/10 rounded-full">
                <Shield className="h-12 w-12 text-blue-600" />
              </div>
            </div>

            <div className="text-center space-y-2">
              <h1 className="text-2xl font-semibold text-gray-900">
                Autenticação de Dois Fatores
              </h1>
              <p className="text-sm text-gray-600">
                Digite o código do Google Authenticator
              </p>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Código de verificação
                </label>
                <Input
                  type="text"
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="text-center text-2xl tracking-widest font-mono h-14"
                  maxLength={6}
                  autoFocus
                  onKeyPress={(e) => e.key === 'Enter' && handleVerify()}
                />
              </div>

              <Button 
                onClick={handleVerify}
                disabled={isVerifying || code.length !== 6}
                className="w-full h-12 text-base font-medium bg-blue-600 hover:bg-blue-700"
              >
                {isVerifying ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Verificando...
                  </>
                ) : (
                  <>
                    <KeyRound className="mr-2 h-5 w-5" />
                    Verificar Código
                  </>
                )}
              </Button>

              <div className="text-center">
                <button
                  onClick={() => setUseBackupCode(!useBackupCode)}
                  className="text-sm text-blue-600 hover:text-blue-700 underline"
                >
                  {useBackupCode ? 'Usar código do app' : 'Usar código de backup'}
                </button>
              </div>
            </div>

            <div className="text-center pt-4 border-t border-gray-200">
              <button
                onClick={() => navigate('/dashboard')}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                Voltar ao dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admin2FAVerification;
